import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowRight, Phone, ShieldCheck, MapPin, Home, Building2, Paintbrush, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyPhone from "@/components/StickyPhone";
import logoAsset from "@/assets/hill-brothers-logo.png.asset.json";

const CANONICAL = "https://hillbrothersnh.com/painters-manchester-nh";
const TITLE = "House Painters in Manchester, NH | Hill Brothers Painting";
const DESCRIPTION =
  "Local painters serving Manchester, NH. Interior, exterior, and commercial painting from a family-run, insured NH crew. Free estimates. Call 603 424 4608.";

const services = [
  {
    icon: Home,
    title: "Interior Painting in Manchester",
    body: "Walls, ceilings, trim, and full-room repaints in Manchester homes from North End Victorians to West Side capes and modern condos downtown. Furniture-safe prep, dustless sanding where possible, and low-VOC paints so families can move back in the same day.",
  },
  {
    icon: Building2,
    title: "Exterior Painting in Manchester",
    body: "Manchester winters are tough on siding and trim. We pressure-wash, scrape, prime bare wood, and use exterior coatings built for the Merrimack Valley's freeze-thaw cycle so the finish lasts well past the next mud season.",
  },
  {
    icon: Paintbrush,
    title: "Commercial Painting in Manchester",
    body: "Offices in the Millyard, retail along Elm Street, and multi-unit properties around the South End. We work after-hours and on weekends to keep your doors open and your tenants happy.",
  },
  {
    icon: Sparkles,
    title: "Cabinet & Trim Refinishing",
    body: "Spray-finished kitchen cabinets and built-ins in a dedicated, ventilated setup. A fraction of the cost of replacement and turned around in days, not weeks.",
  },
];

const reasons = [
  {
    title: "Insured & Registered in New Hampshire",
    body: "Fully insured for liability and workers' comp. Registered with the State of New Hampshire. Certificates available on request before work begins.",
  },
  {
    title: "Family-Run, No Subcontractors",
    body: "Every Manchester project is staffed by Hill Brothers crew members we know by name. The person who quotes your job is on site while it is being done.",
  },
  {
    title: "Local to the Manchester Area",
    body: "Based minutes south in Merrimack, NH. Same-day site visits in Manchester are routine and there is no travel surcharge baked into your estimate.",
  },
  {
    title: "Honest, Itemized Estimates",
    body: "Line-item pricing for prep, primer, paint, and labor. No upsells, no surprise change orders. References from recent Manchester jobs available on request.",
  },
];

const steps = [
  { n: "1", title: "Get in touch", body: "Call 603 424 4608 or send a message. We respond same-day during business hours." },
  { n: "2", title: "On-site estimate", body: "Free walk-through at your Manchester home or business. Clear, written quote sent over as soon as we have walked the job." },
  { n: "3", title: "Schedule the work", body: "Pick a start date that fits your life. We will give you a realistic timeline based on our current schedule." },
  { n: "4", title: "Paint & walkthrough", body: "Daily clean-up, end-of-job inspection together, and a touch-up kit left behind." },
];

const faqs = [
  {
    q: "How quickly can you start a project in Manchester?",
    a: "Timing depends on our current workload and the season. Reach out and we will give you a straight answer on when we can realistically get started, and it is always smart to book exterior work early in the season.",
  },
  {
    q: "Do you paint historic and older homes in Manchester?",
    a: "Yes. Many Manchester neighborhoods, especially the North End and around the Millyard, have homes built well before 1978, so we take extra care with prep, containment, and clean-up to keep the job safe for your family and easy on your property.",
  },
  {
    q: "Can you handle HOA or condo color approvals?",
    a: "We are happy to provide color samples, manufacturer data sheets, and any documentation your HOA or condo association needs for approval before scheduling.",
  },
  {
    q: "Are you insured and registered in New Hampshire?",
    a: "Yes. Hill Brothers Painting & Contracting LLC is fully insured for general liability and workers' compensation, and registered with the State of New Hampshire. We will provide certificates on request.",
  },
  {
    q: "Do you offer free estimates in Manchester?",
    a: "Yes. On-site estimates in Manchester are free with no obligation, and we follow up with a clear written quote shortly after the visit.",
  },
  {
    q: "What areas around Manchester do you serve?",
    a: "Beyond Manchester we regularly work in Bedford, Goffstown, Hooksett, Auburn, Londonderry, Merrimack, Litchfield, and the surrounding southern NH communities.",
  },
];

const PaintersManchesterNH = () => {
  const localBusinessJsonLd = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "PaintingService"],
    name: "Hill Brothers Painting & Contracting",
    image: "https://hillbrothersnh.com/og-image.png",
    "@id": "https://hillbrothersnh.com/#business",
    url: CANONICAL,
    telephone: "+1-603-424-4608",
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Merrimack",
      addressRegion: "NH",
      addressCountry: "US",
    },
    areaServed: [
      { "@type": "City", name: "Manchester, NH" },
      { "@type": "City", name: "Bedford, NH" },
      { "@type": "City", name: "Goffstown, NH" },
      { "@type": "City", name: "Hooksett, NH" },
      { "@type": "City", name: "Merrimack, NH" },
    ],
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "17:00",
      },
    ],
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://hillbrothersnh.com/" },
      { "@type": "ListItem", position: 2, name: "Painters in Manchester, NH", item: CANONICAL },
    ],
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL} />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <script type="application/ld+json">{JSON.stringify(localBusinessJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <Header />

      <main className="pt-20">
        {/* Hero */}
        <section className="bg-primary py-16">
          <div className="section-container text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <img
              src={logoAsset.url}
              alt="Hill Brothers Painting & Contracting logo"
              width="720"
              height="720"
              loading="eager"
              decoding="async"
              className="w-44 md:w-52 h-auto mb-4 rounded-xl mx-auto"
            />
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary-foreground mb-4">
              House Painters in Manchester, NH
            </h1>
            <p className="text-xl text-primary-foreground/85 max-w-2xl mb-8 mx-auto">
              Family-run, insured, and based minutes south of Manchester in Merrimack, NH. Interior, exterior, and commercial painting done right the first time.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="tel:+16034244608"
                className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3 rounded-md font-semibold hover:bg-accent/90 transition-colors"
              >
                <Phone className="w-5 h-5" aria-hidden="true" />
                603 424 4608
              </a>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 bg-card text-foreground px-6 py-3 rounded-md font-semibold hover:bg-card/90 transition-colors"
              >
                Get a Free Estimate
                <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* Local intro */}
        <section className="py-16 bg-background">
          <div className="section-container max-w-3xl">
            <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
              Painting Manchester Homes & Businesses
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Manchester is a mix of historic North End Victorians, brick mill buildings turned condos along the Merrimack, mid-century homes on the West Side, and busy commercial space along Elm Street and South Willow. No two jobs are the same, and a painter who paints them all the same way gets it wrong.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We are based in Merrimack, just south of the city. That means a Hill Brothers crew can be at your Manchester property the same day for an estimate, and on the job without the windshield-time surcharge bigger out-of-state outfits build into their quotes.
            </p>
          </div>
        </section>

        {/* Services */}
        <section className="py-16 bg-secondary/30">
          <div className="section-container">
            <h2 className="text-3xl font-heading font-bold text-foreground mb-8 text-center">
              Painting Services in Manchester
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map((s) => (
                <article key={s.title} className="bg-card rounded-lg p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                      <s.icon className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-foreground">{s.title}</h3>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">{s.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Why us */}
        <section className="py-16 bg-background">
          <div className="section-container">
            <h2 className="text-3xl font-heading font-bold text-foreground mb-8 text-center">
              Why Manchester Homeowners Choose Hill Brothers
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {reasons.map((r) => (
                <div key={r.title} className="flex gap-4">
                  <ShieldCheck className="w-6 h-6 text-primary flex-shrink-0 mt-1" aria-hidden="true" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">{r.title}</h3>
                    <p className="text-muted-foreground">{r.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-16 bg-secondary/30">
          <div className="section-container">
            <h2 className="text-3xl font-heading font-bold text-foreground mb-8 text-center">
              How Your Manchester Project Works
            </h2>
            <ol className="grid md:grid-cols-4 gap-6">
              {steps.map((step) => (
                <li key={step.n} className="bg-card rounded-lg p-6">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold mb-3">
                    {step.n}
                  </div>
                  <h3 className="font-heading font-semibold text-foreground mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Service area */}
        <section className="py-16 bg-background">
          <div className="section-container max-w-3xl">
            <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
              Service Area Around Manchester
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              In addition to all of Manchester, we regularly paint homes and businesses in:
            </p>
            <ul className="flex flex-wrap gap-3">
              {["Bedford, NH", "Goffstown, NH", "Hooksett, NH", "Auburn, NH", "Londonderry, NH", "Merrimack, NH", "Litchfield, NH", "Candia, NH", "Nashua, NH"].map((city) => (
                <li
                  key={city}
                  className="inline-flex items-center gap-2 bg-secondary text-secondary-foreground px-4 py-2 rounded-full text-sm"
                >
                  <MapPin className="w-4 h-4 text-primary" aria-hidden="true" />
                  {city}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-secondary/30">
          <div className="section-container max-w-3xl">
            <h2 className="text-3xl font-heading font-bold text-foreground mb-8 text-center">
              Manchester Painting FAQ
            </h2>
            <div className="space-y-4">
              {faqs.map((f) => (
                <details
                  key={f.q}
                  className="bg-card rounded-lg p-5 group"
                >
                  <summary className="font-heading font-semibold text-foreground cursor-pointer list-none flex justify-between items-center">
                    {f.q}
                    <span className="text-primary text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-primary">
          <div className="section-container text-center max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-4">
              Ready for a Free Manchester Estimate?
            </h2>
            <p className="text-lg text-primary-foreground/85 mb-8">
              Call us or send a quick message. We will be in touch the same business day.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="tel:+16034244608"
                className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3 rounded-md font-semibold hover:bg-accent/90 transition-colors"
              >
                <Phone className="w-5 h-5" aria-hidden="true" />
                603 424 4608
              </a>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 bg-card text-foreground px-6 py-3 rounded-md font-semibold hover:bg-card/90 transition-colors"
              >
                Message Us
                <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <StickyPhone />
    </div>
  );
};

export default PaintersManchesterNH;
